import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/es'
import 'dayjs/locale/fr'
import 'dayjs/locale/it'
import 'dayjs/locale/pl'
import 'dayjs/locale/pt'
import calendar from 'dayjs/plugin/calendar'
import isBetween from 'dayjs/plugin/isBetween'
import isYesterday from 'dayjs/plugin/isYesterday'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import updateLocale from 'dayjs/plugin/updateLocale'
import utc from 'dayjs/plugin/utc'
import { createIntl } from 'react-intl'

import type { TT } from '@/src/lang/useTranslate'

import { getDateWithoutTimezone } from '../../utils/getDateWithoutTimezone'
import type { TDateArg, TDateHyphenYYYYMMDD, TNumChar } from './date.types'

import { allMessages, locales } from '@/translations/translations.constants'

dayjs.extend(calendar)
dayjs.extend(isYesterday)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.extend(isBetween)
dayjs.extend(localeData)
dayjs.extend(utc)
dayjs.extend(timezone)

const localeList = dayjs.Ls

// This changes the strings returned by dayjs for relative time (using the 'fromNow' method)
const relativeTimeSetup = (locale: TLocale) => {
  const intl = createIntl({ locale, messages: allMessages[locale] })
  return {
    // This changes the string returned by dayjs for 'a few seconds ago'
    s: intl.formatMessage({ id: 'conversation.few-seconds' }),
  }
}

for (const locale of locales) {
  if (localeList[locale])
    dayjs.updateLocale(locale, {
      relativeTime: {
        ...localeList[locale].relativeTime,
        ...relativeTimeSetup(locale),
      },
    })
  else console.warn(`Locale ${locale} is not supported by dayjs`)
}

export const setupDayjs = (locale: TLocale) => {
  dayjs.locale(locale)
}

/** Import this instead of calling dayjs.tz.guess() directly, so the timezone plugin is guaranteed to be registered */
export const guessTimezone = () => dayjs.tz.guess()

type TDateFormat = {
  dayMonthYear: string
  dayMonth: string
  monthYear: string
  shortDayMonth: string
  shortDayMonthYear: string
  prefix: string
}

export const LocaleDateFormat: Record<TLocale, TDateFormat> & Partial<Record<string, TDateFormat>> = {
  fr: {
    dayMonthYear: 'D MMMM YYYY',
    dayMonth: 'D MMMM',
    monthYear: 'MMMM YYYY',
    shortDayMonth: 'D MMM',
    shortDayMonthYear: 'D MMM YYYY',
    prefix: 'Le ',
  },
  en: {
    dayMonthYear: 'MMMM D, YYYY',
    dayMonth: 'MMMM D',
    monthYear: 'MMMM YYYY',
    shortDayMonth: 'MMM D',
    shortDayMonthYear: 'MMM D, YYYY',
    prefix: 'On ',
  },
  es: {
    dayMonthYear: 'D [de] MMMM [de] YYYY',
    dayMonth: 'D [de] MMMM',
    monthYear: 'MMMM [de] YYYY',
    shortDayMonth: 'D [de] MMM',
    shortDayMonthYear: 'D [de] MMM [de] YYYY',
    prefix: 'El ',
  },
  pt: {
    dayMonthYear: 'D [de] MMMM [de] YYYY',
    dayMonth: 'D [de] MMMM',
    monthYear: 'MMMM [de] YYYY',
    shortDayMonth: 'D [de] MMM',
    shortDayMonthYear: 'D [de] MMM [de] YYYY',
    prefix: 'Em ',
  },
  it: {
    dayMonthYear: 'YYYY [m.] MMMM D [d.]',
    dayMonth: '[m.] MMMM D [d.]',
    monthYear: 'MMMM YYYY',
    shortDayMonth: 'MMM D',
    shortDayMonthYear: 'MMM D, YYYY',
    prefix: 'Il ',
  },
  pl: {
    dayMonthYear: 'D MMMM YYYY',
    dayMonth: 'D MMMM',
    monthYear: 'MMMM YYYY',
    shortDayMonth: 'D MMM',
    shortDayMonthYear: 'D MMM YYYY',
    prefix: 'W dniu ',
  },
}

/** Number of days between two dates */
export const getDiffInDays = (fromDate: TDateArg, toDate: TDateArg) => {
  const from = getDateWithoutTimezone(fromDate) // No hours, no timezone: pure date
  const to = getDateWithoutTimezone(toDate)

  return to?.diff(from, 'day') || 0
}

/**
 * Time between two datetimes, in days
 * Ex: -1.345
 *
 * For the number of days between two dates, use getDiffInDays
 */
export const getStrictDiffInDays = (fromDate: TDateArg, toDate: TDateArg) => {
  const from = dayjs(fromDate)
  const to = dayjs(toDate)
  return to.diff(from, 'day', true)
}

/** Format time to calendar format */
export const formatCalendar = (date: TDateArg | Dayjs, t: TT, withPrefix: boolean = false) =>
  dayjs(date).calendar(null, {
    sameDay: `[${t('word.today')}]`, // "Today"
    lastDay: `[${t('word.yesterday')}]`, // "Yesterday"
    lastWeek: 'dddd', // "Monday"
    sameElse: function callback(this: Dayjs, now: Dayjs) {
      const locale = now.locale()
      const prefix = withPrefix ? LocaleDateFormat[locale]?.prefix : ''
      const formattedDate =
        now.year() !== this?.year()
          ? this.format(LocaleDateFormat[locale]?.dayMonthYear)
          : this.format(LocaleDateFormat[locale]?.dayMonth)
      return `${prefix}${formattedDate}`
    },
  })

export const getLocaleDateFormat = (currentDate?: TDateArg) => {
  if (!currentDate) return ''
  const date = dayjs(currentDate)
  const now = dayjs(Date.now())
  return now.year() !== date?.year()
    ? date.format(LocaleDateFormat[now.locale()]?.dayMonthYear)
    : date.format(LocaleDateFormat[now.locale()]?.dayMonth)
}

export const getDayOfWeek = (dayIndex: number) => {
  // Reminder : 0 is Sunday 1 is Monday, 6 is Saturday
  return dayjs.weekdays()[dayIndex]
}

export const isSameDayAndMonth = (first: Date, second: Date) => {
  const date1 = dayjs(first)
  const date2 = dayjs(second)

  return date1.get('date') === date2.get('date') && date1.get('month') === date2.get('month')
}

export const dateToYYYYMMDD = (date: Date): TDateHyphenYYYYMMDD => {
  const year = String(date.getUTCFullYear()).padStart(4, '0') as `${number}`
  const month = String(date.getUTCMonth() + 1).padStart(2, '0') as `${number}`
  const day = String(date.getUTCDate()).padStart(2, '0') as `${TNumChar}${TNumChar}`

  return `${year}-${month}-${day}`
}

export const isValidDate = (date: Date) => {
  return date instanceof Date && !isNaN(date.getTime())
}

export const toHumanDate = (date: Date, locale: string) => {
  return dayjs(date).locale(locale).format('LL')
}

export const isDateBefore = (date: Date, dateToCompare: Date): boolean => {
  if (date.getUTCFullYear() < dateToCompare.getUTCFullYear()) return true
  if (date.getUTCFullYear() > dateToCompare.getUTCFullYear()) return false

  if (date.getUTCMonth() < dateToCompare.getUTCMonth()) return true
  if (date.getUTCMonth() > dateToCompare.getUTCMonth()) return false

  if (date.getUTCDate() < dateToCompare.getUTCDate()) return true
  return false
}

export const toFirstCharOfDay = (date: TDateArg, locale: string) =>
  dayjs(date)
    .locale(locale || 'fr')
    .format('dd')
    .charAt(0)
